import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Breadcrumb from '../Components/Breadcrumb';
import Footer from '../Components/Footer';
import Header from '../Components/Header';
import LayerNavigation from '../Components/LayerNavigation';
import categoryBannerPlaceholder from '../assests/img/category-banner-placeholder.jpg';

const API = 'https://admin.bridge365.in/wp-json/wc/v3';
const CK = "ck_1d0bfb52b9046a39fb2d906f89f691968931bce6";
const CS = "cs_956c51f2c9249f343bec7f0ecbfd7738654ba424";

function Shop() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") || "";

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(id || null);
  const [categoryName, setCategoryName] = useState('All Products');
  const [categoryImage, setCategoryImage] = useState(null);
  const [filters, setFilters] = useState({
    sellers: [],
    minPrice: "",
    maxPrice: "",
    rating: null,
    attributes: {},
  });
  const [sortBy, setSortBy] = useState("popularity");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    setActiveCat(id || null);
  }, [id]);

  useEffect(() => {
    fetch(
      `${API}/products/categories?per_page=100&consumer_key=${CK}&consumer_secret=${CS}`
    )
      .then((res) => res.json())
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    let url = `${API}/products?per_page=100&consumer_key=${CK}&consumer_secret=${CS}`;

    if (activeCat) {
      url += `&category=${activeCat}`;
    }

    if (searchQuery) {
      url += `&search=${encodeURIComponent(searchQuery)}`;
    }

    fetch(url)
      .then((res) => res.json())
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]));
  }, [activeCat, searchQuery]);

  useEffect(() => {
    if (!activeCat) {
      setCategoryName('All Products');
      setCategoryImage(null);
      return;
    }

    fetch(
      `${API}/products/categories/${activeCat}?consumer_key=${CK}&consumer_secret=${CS}`
    )
      .then((res) => res.json())
      .then((data) => {
        setCategoryName(data?.name || 'All Products');
        setCategoryImage(data?.image?.src || null);
      })
      .catch(() => {
        setCategoryName('All Products');
        setCategoryImage(null);
      });
  }, [activeCat]);

  useEffect(() => {
    setFilters({
      sellers: [],
      minPrice: "",
      maxPrice: "",
      rating: null,
      attributes: {},
    });
    setIsFilterOpen(false);
  }, [activeCat]);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      if (filters.sellers.length > 0 && !filters.sellers.includes(product.seller?.id)) {
        return false;
      }

      const price = Number(product.price || product.regular_price || 0);
      if (filters.minPrice !== "" && price < Number(filters.minPrice)) {
        return false;
      }
      if (filters.maxPrice !== "" && price > Number(filters.maxPrice)) {
        return false;
      }

      if (filters.rating && Number(product.average_rating || 0) < filters.rating) {
        return false;
      }

      return Object.entries(filters.attributes).every(([name, values]) => {
        if (!values.length) return true;

        const attribute = (product.attributes || []).find(
          (item) => item.name === name || item.name?.replace(/&amp;/g, "&") === name
        );
        const options = attribute
          ? (Array.isArray(attribute.options)
              ? attribute.options
              : [attribute.option || attribute.value].filter(Boolean))
          : [];

        const normalized = options.map((option) => String(option).toLowerCase());
        return values.some((value) => normalized.includes(String(value).toLowerCase()));
      });
    });
  }, [products, filters]);

  const sortedProducts = useMemo(() => {
    const list = [...filteredProducts];

    const getPrice = (product) => Number(product.price || product.regular_price || 0);

    switch (sortBy) {
      case "price-low":
        return list.sort((a, b) => getPrice(a) - getPrice(b));
      case "price-high":
        return list.sort((a, b) => getPrice(b) - getPrice(a));
      case "newest":
        return list.sort(
          (a, b) => new Date(b.date_created || 0) - new Date(a.date_created || 0)
        );
      case "name":
        return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      case "rating":
        return list.sort(
          (a, b) => Number(b.average_rating || 0) - Number(a.average_rating || 0)
        );
      default:
        return list.sort(
          (a, b) => Number(b.total_sales || 0) - Number(a.total_sales || 0)
        );
    }
  }, [filteredProducts, sortBy]);

  function decodeHTML(html) {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

  return (
    <div>
      <Helmet>
        <title>{searchQuery ? `Search: ${searchQuery}` : decodeHTML(categoryName)}</title>
      </Helmet>

      <Header />

      <div className="container main-contant shop-page">
        <div className="inner-page-container shop-page-container">
            <Breadcrumb categoryId={activeCat} categories={categories} />

          <div className={`shop-category-banner${!categoryImage ? " is-placeholder" : ""}`}>
            <img
              src={categoryImage || categoryBannerPlaceholder}
              alt={decodeHTML(categoryName)}
              className="shop-category-banner-image"
            />
            {!categoryImage && (
              <h2 className="shop-category-banner-title">{decodeHTML(categoryName)}</h2>
            )}
          </div>

          <h1 className="page-title shop-title hidden">{decodeHTML(categoryName)}</h1>
        

          <div className="shop-layout">
            <LayerNavigation
              categories={categories}
              activeCat={activeCat}
              products={products}
              filters={filters}
              onChangeFilters={setFilters}
              isOpen={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
            />

            <section className="shop-products-area">
              <div className="shop-toolbar">
                <button
                  type="button"
                  className="shop-filter-toggle"
                  onClick={() => setIsFilterOpen(true)}
                >
                  Filter +
                </button>

                <p className="shop-product-count shop-product-count-top">
                  Showing <strong>{sortedProducts.length}</strong>{" "}
                  {sortedProducts.length === 1 ? "product" : "products"}
                </p>

                <label className="shop-sort">
                  <span>Sort By</span>
                  <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                    <option value="popularity">Popularity</option>
                    <option value="newest">Newest</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="name">Name: A to Z</option>
                    <option value="rating">Rating</option>
                  </select>
                </label>
              </div>

              {sortedProducts.length === 0 ? (
                <div className="shop-empty-state">Product not found!</div>
              ) : (
                <div className="shop-products-grid">
                  {sortedProducts.map((product) => (
                    <article className="shop-product-card" key={product.id}>
                      <Link
                        className="shop-product-link"
                        to={`/product/${product.id}`}
                      >
                        <div className="shop-product-image-wrap">
                          <img
                            src={product.images[0]?.src}
                            alt={product.name}
                            className="shop-product-image"
                          />
                        </div>

                        <h4 className="shop-product-title">{product.name}</h4>

                         </Link>

                        <div className="shop-product-price">
                          ₹ {product.price || product.regular_price || ''}
                          <span>/Piece</span>
                        </div>

                           <Link
            to={`/seller/${product.seller.id}`}
           className="shop-contact-supplier" 
        >
            View Seller
        </Link>

                   

                        <div className="shop-supplier-meta">
                          <p>{product.seller?.shop_name || 'Verified Supplier'}</p>
                          <strong>New Delhi</strong>
                          <div className="shop-trust-row">
                            <span>GST</span>
                            <span>Email</span>
                            <span>Mobile</span>
                          </div>
                          <div className="shop-rating-row">
                            <span>★★★★★</span>
                            <strong>4.8</strong>
                            <small>({product.rating_count || 18})</small>
                          </div>
                        </div>
                    

                             
                    </article>
                  ))}
                </div>
              )}

              <p className="shop-product-count shop-product-count-bottom">
                Showing <strong>{sortedProducts.length}</strong>{" "}
                {sortedProducts.length === 1 ? "product" : "products"}
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Shop;
